import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  X, 
  Bed, 
  Bath, 
  Ruler, 
  Calendar, 
  TrendingUp, 
  DollarSign,
  Home,
  MapPin,
  Percent,
  Clock,
  AlertTriangle,
  CheckCircle2,
  MinusCircle
} from "lucide-react";
import { usePropertyComparison } from "@/hooks/use-property-comparison";
import { MockProperty, propertyTypeLabels, statusLabels } from "@/data/mockProperties";
import { cn } from "@/lib/utils";

interface PropertyComparisonDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PropertyComparisonDrawer({ open, onOpenChange }: PropertyComparisonDrawerProps) {
  const { selectedProperties, removeProperty } = usePropertyComparison();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat("en-US").format(value);

  // Find best values for highlighting
  const getBestValue = (key: keyof MockProperty, higher = true) => {
    const values = selectedProperties.map(p => p[key] as number).filter(v => typeof v === 'number');
    if (values.length === 0) return null;
    return higher ? Math.max(...values) : Math.min(...values);
  };

  const bestPrice = getBestValue('price', false);
  const bestCapRate = getBestValue('capRate', true);
  const bestCashOnCash = getBestValue('cashOnCash', true);
  const bestRent = getBestValue('estimatedRent', true);
  const bestSqft = getBestValue('sqft', true);
  const lowestDaysOnMarket = getBestValue('daysOnMarket', false);

  const ComparisonCell = ({ 
    value, 
    isBest, 
    children 
  }: { 
    value?: number | string | boolean; 
    isBest?: boolean;
    children: React.ReactNode;
  }) => (
    <div className={cn(
      "text-center py-2 px-3 rounded-md transition-colors",
      isBest && "bg-success/10 text-success font-semibold"
    )}>
      {children}
    </div>
  );

  const MetricRow = ({ 
    label, 
    icon: Icon, 
    values,
    bestValue,
    format = (v) => String(v),
    higherIsBetter = true
  }: {
    label: string;
    icon: React.ElementType;
    values: (number | string | undefined)[];
    bestValue?: number | null;
    format?: (value: number | string | undefined) => string;
    higherIsBetter?: boolean;
  }) => (
    <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}>
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      {values.map((value, idx) => {
        const numValue = typeof value === 'number' ? value : undefined;
        const isBest = bestValue !== null && numValue === bestValue;
        return (
          <ComparisonCell key={idx} isBest={isBest}>
            <span className="text-sm">{format(value)}</span>
          </ComparisonCell>
        );
      })}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] p-0">
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2">
            Property Comparison
            <Badge variant="secondary">{selectedProperties.length} properties</Badge>
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(85vh-80px)]">
          <div className="p-6">
            {selectedProperties.length < 2 ? (
              <div className="text-center py-12 text-muted-foreground">
                Select at least 2 properties to compare
              </div>
            ) : (
              <div className="space-y-6">
                {/* Property Headers */}
                <div 
                  className="grid gap-4 sticky top-0 bg-background z-10 pb-4" 
                  style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}
                >
                  <div />
                  {selectedProperties.map((property) => (
                    <div 
                      key={property.id} 
                      className="relative bg-card border border-border rounded-lg p-4"
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6"
                        onClick={() => removeProperty(property.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="aspect-video rounded-md overflow-hidden mb-3">
                        <img 
                          src={property.imageUrl} 
                          alt={property.address}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-foreground text-sm truncate pr-6">
                        {property.address}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {property.city}, {property.state} {property.zip}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Price & Status Section */}
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Price & Status</h4>
                  <MetricRow
                    label="List Price"
                    icon={DollarSign}
                    values={selectedProperties.map(p => p.price)}
                    bestValue={bestPrice}
                    format={(v) => formatCurrency(v as number)}
                    higherIsBetter={false}
                  />
                  <MetricRow
                    label="Property Type"
                    icon={Home}
                    values={selectedProperties.map(p => propertyTypeLabels[p.propertyType])}
                  />
                  <MetricRow
                    label="Status"
                    icon={Clock}
                    values={selectedProperties.map(p => statusLabels[p.status])}
                  />
                  <MetricRow
                    label="Days on Market"
                    icon={Calendar}
                    values={selectedProperties.map(p => p.daysOnMarket)}
                    bestValue={lowestDaysOnMarket}
                    format={(v) => `${v} days`}
                    higherIsBetter={false}
                  />
                </div>

                <Separator />

                {/* Property Details */}
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Property Details</h4>
                  <MetricRow
                    label="Bedrooms"
                    icon={Bed}
                    values={selectedProperties.map(p => p.bedrooms)}
                    format={(v) => `${v} bd`}
                  />
                  <MetricRow
                    label="Bathrooms"
                    icon={Bath}
                    values={selectedProperties.map(p => p.bathrooms)}
                    format={(v) => `${v} ba`}
                  />
                  <MetricRow
                    label="Square Feet"
                    icon={Ruler}
                    values={selectedProperties.map(p => p.sqft)}
                    bestValue={bestSqft}
                    format={(v) => `${formatNumber(v as number)} sqft`}
                  />
                  <MetricRow
                    label="Year Built"
                    icon={Calendar}
                    values={selectedProperties.map(p => p.yearBuilt)}
                    format={(v) => String(v)}
                  />
                  <MetricRow
                    label="Price/Sqft"
                    icon={DollarSign}
                    values={selectedProperties.map(p => Math.round(p.price / p.sqft))}
                    format={(v) => `$${v}/sqft`}
                    higherIsBetter={false}
                  />
                </div>

                <Separator />

                {/* Investment Metrics */}
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Investment Metrics</h4>
                  <MetricRow
                    label="Est. Rent"
                    icon={DollarSign}
                    values={selectedProperties.map(p => p.estimatedRent)}
                    bestValue={bestRent}
                    format={(v) => `${formatCurrency(v as number)}/mo`}
                  />
                  <MetricRow
                    label="Cap Rate"
                    icon={Percent}
                    values={selectedProperties.map(p => p.capRate)}
                    bestValue={bestCapRate}
                    format={(v) => `${v}%`}
                  />
                  <MetricRow
                    label="Cash-on-Cash"
                    icon={TrendingUp}
                    values={selectedProperties.map(p => p.cashOnCash)}
                    bestValue={bestCashOnCash}
                    format={(v) => `${v}%`}
                  />
                  <MetricRow
                    label="GRM"
                    icon={TrendingUp}
                    values={selectedProperties.map(p => p.grossRentMultiplier)}
                    format={(v) => `${v}x`}
                    higherIsBetter={false}
                  />
                </div>

                <Separator />

                {/* Deal Indicators */}
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Deal Indicators</h4>
                  <div 
                    className="grid gap-4" 
                    style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <AlertTriangle className="h-4 w-4" />
                      Distress Signals
                    </div>
                    {selectedProperties.map((property) => (
                      <div key={property.id} className="flex flex-wrap gap-1 justify-center py-2">
                        {property.isForeclosure && (
                          <Badge variant="destructive" className="text-xs">Foreclosure</Badge>
                        )}
                        {property.isPreForeclosure && (
                          <Badge className="bg-warning text-warning-foreground text-xs">Pre-Foreclosure</Badge>
                        )}
                        {property.isAuction && (
                          <Badge variant="secondary" className="text-xs">Auction</Badge>
                        )}
                        {property.isReo && (
                          <Badge variant="outline" className="text-xs">REO</Badge>
                        )}
                        {!property.isForeclosure && !property.isPreForeclosure && !property.isAuction && !property.isReo && (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div 
                    className="grid gap-4" 
                    style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <TrendingUp className="h-4 w-4" />
                      Price Reduced
                    </div>
                    {selectedProperties.map((property) => (
                      <div key={property.id} className="flex justify-center py-2">
                        {property.priceReduction ? (
                          <Badge className="bg-success/10 text-success border-success/20 text-xs">
                            -{formatCurrency(property.priceReduction)}
                          </Badge>
                        ) : (
                          <MinusCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div 
                    className="grid gap-4" 
                    style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Below Market Value
                    </div>
                    {selectedProperties.map((property) => (
                      <div key={property.id} className="flex justify-center py-2">
                        {property.belowMarketValue ? (
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        ) : (
                          <MinusCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div 
                    className="grid gap-4" 
                    style={{ gridTemplateColumns: `180px repeat(${selectedProperties.length}, 1fr)` }}
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <AlertTriangle className="h-4 w-4" />
                      Motivated Seller
                    </div>
                    {selectedProperties.map((property) => (
                      <div key={property.id} className="flex justify-center py-2">
                        {property.motivatedSeller ? (
                          <CheckCircle2 className="h-5 w-5 text-warning" />
                        ) : (
                          <MinusCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
